import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, CheckCircle2, PlayCircle, Building2, User } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const STATUS_CONFIG = {
    'todo': { label: 'To Do', icon: Clock, color: 'bg-slate-500/10 text-slate-600 border-slate-200' },
    'in_progress': { label: 'In Progress', icon: PlayCircle, color: 'bg-blue-500/10 text-blue-700 border-blue-200' },
    'done': { label: 'Completed', icon: CheckCircle2, color: 'bg-green-500/10 text-green-700 border-green-200' }
};

export function TaskCard({ task, deals, teamMembers, onStatusChange, onClick }) {
    const StatusIcon = STATUS_CONFIG[task.status]?.icon || Clock;

    // Find associated deal name
    const relatedDeal = deals?.find(d => d._id === task.deal_id);

    // Find assigned members
    const assignedMembers = teamMembers?.filter(m => task.assignee_ids?.includes(m._id)) || [];

    return (
        <Card
            className="w-full hover:shadow-md transition-shadow cursor-pointer bg-card/50 backdrop-blur-sm border-muted/60"
            onClick={onClick}
        >
            <CardHeader className="p-4 pb-2">
                <div className="flex justify-between items-start gap-2">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <CardTitle className="text-base font-semibold leading-tight line-clamp-2 text-left">
                                    {task.title}
                                </CardTitle>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[300px]">
                                <p>{task.title}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </CardHeader>
            <CardContent className="p-4 pt-0 pb-3">
                {task.description && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <p className="text-sm text-muted-foreground line-clamp-1 mb-3 text-left">
                                    {task.description}
                                </p>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[300px] max-h-[200px] overflow-y-auto">
                                <p className="text-sm whitespace-pre-wrap">{task.description}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}

                <div className="flex flex-col gap-2 mt-2">
                    {relatedDeal && (
                        <div className="flex items-center text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md w-fit">
                            <Building2 className="h-3 w-3 mr-1" />
                            <span className="truncate max-w-[150px]">{relatedDeal.title}</span>
                        </div>
                    )}

                    {assignedMembers.length > 0 && (
                        <div className="flex items-center text-xs text-muted-foreground mt-1">
                            <div className="flex -space-x-2 mr-2">
                                {assignedMembers.slice(0, 3).map((member, i) => (
                                    <div
                                        key={member._id}
                                        className="h-6 w-6 rounded-full bg-primary/10 border-2 border-background flex items-center justify-center relative group"
                                        title={member.name}
                                    >
                                        <span className="text-[10px] font-medium text-primary">
                                            {member.name.substring(0, 2).toUpperCase()}
                                        </span>
                                    </div>
                                ))}
                                {assignedMembers.length > 3 && (
                                    <div className="h-6 w-6 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                                        <span className="text-[10px] font-medium text-muted-foreground">
                                            +{assignedMembers.length - 3}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>

            <CardFooter className="p-3 pt-0 flex justify-between items-center border-t border-muted/30 mt-2 bg-muted/10">
                <Badge variant="outline" className={`gap-1 px-2 py-0.5 mt-2 ${STATUS_CONFIG[task.status]?.color}`}>
                    <StatusIcon className="h-3 w-3" />
                    <span className="text-xs font-medium">{STATUS_CONFIG[task.status]?.label}</span>
                </Badge>

                <select
                    className="text-xs bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer focus:ring-0 mt-2"
                    value={task.status}
                    onChange={(e) => onStatusChange(task._id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                >
                    <option value="todo">Move to To-Do</option>
                    <option value="in_progress">Move to In Progress</option>
                    <option value="done">Mark as Done</option>
                </select>
            </CardFooter>
        </Card>
    );
}
